import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import ActionStatusPanel from "../../components/admin/ActionStatusPanel";
import CommercialPanel from "../../components/admin/CommercialPanel";
import UsersPanel from "../../components/admin/UsersPanel";
import Skeleton from "../../components/states/Skeleton";
import {
  CORE_SERVICES,
  listAuditEntries,
  requestAdminAction,
  TIER_OPTIONS,
  type AdminActionType,
  type AdminTier,
  type AuditEntry,
} from "../../lib/admin/adminActions.mjs";
import { listCustomers, type CustomerRow, type Tier } from "../../lib/admin/customers";
import {
  deriveConnectorHealth,
  fetchEntityHealth,
  fetchEntityRowCount,
  fetchEntitySyncHistory,
  fetchRecentSyncEvents,
  fetchTableStats,
  isEntityCountStale,
  type ConnectorHealth,
  type EntityHealthRow,
  type RecentSyncEvent,
  type TableStats,
} from "../../lib/admin/health";
import { classifySyncError } from "../../lib/admin/syncRemediation.mjs";
import { formatLiveRowCount } from "../../lib/admin/entityRows.mjs";
import { startImpersonation } from "../../lib/impersonate";
import { humanize } from "../../lib/labels";
import { getSupabase } from "../../lib/supabase";

/**
 * MER-198: Admin Console customer detail page at `/admin/customers/:slug`.
 *
 * Three stacked sections:
 *   1. Header: name, slug, tier, "View as" button, services count.
 *   2. Pipeline health: connector status + canonical entity health,
 *      side by side. Both read from `sync_log` via the helpers in
 *      `lib/admin/health.ts`.
 *   3. Recent ingests: last 20 sync_log rows for the tenant.
 *
 * v1 scope deliberately omits:
 *   - Sign-in count over 30 days (needs another SECURITY DEFINER RPC).
 *   - Connector configuration UI (read-only per the MER-185 epic).
 *   - Commercial state panel (MER-212 wires this in between sections).
 * Both flagged in the PR description as follow-ups.
 */

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const SEVEN_DAYS_MS = 7 * ONE_DAY_MS;

const TIER_LABELS: Record<Tier, string> = {
  essential: "Essential",
  growth: "Growth",
  professional: "Pro",
  enterprise: "Enterprise",
};

const TIER_TONE: Record<Tier, { bg: string; fg: string }> = {
  essential: { bg: "rgba(38,168,159,0.06)", fg: "var(--brand-deep)" },
  growth: { bg: "rgba(38,168,159,0.10)", fg: "var(--brand-deep)" },
  professional: { bg: "rgba(15,107,92,0.10)", fg: "var(--brand-deep)" },
  enterprise: { bg: "rgba(15,107,92,0.18)", fg: "var(--brand-deep)" },
};

function relativeTime(iso: string | null): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "Never";
  const delta = Date.now() - t;
  if (delta < 60 * 1000) return "just now";
  if (delta < ONE_HOUR_MS) {
    const mins = Math.max(1, Math.round(delta / (60 * 1000)));
    return `${mins}m ago`;
  }
  if (delta < ONE_DAY_MS) {
    return `${Math.round(delta / ONE_HOUR_MS)}h ago`;
  }
  return `${Math.round(delta / ONE_DAY_MS)}d ago`;
}

function freshnessTone(iso: string | null): string {
  if (!iso) return "var(--text-muted)";
  const delta = Date.now() - new Date(iso).getTime();
  if (delta < ONE_DAY_MS) return "var(--flag-green)";
  if (delta < SEVEN_DAYS_MS) return "var(--flag-yellow)";
  return "var(--flag-red)";
}

interface PageState {
  customer: CustomerRow | null;
  entityHealth: EntityHealthRow[];
  recentEvents: RecentSyncEvent[];
  isLoading: boolean;
  error: string | null;
}

export default function CustomerDetail() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug ?? "";
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({
    customer: null,
    entityHealth: [],
    recentEvents: [],
    isLoading: true,
    error: null,
  });
  const [impersonating, setImpersonating] = useState(false);
  const [impError, setImpError] = useState<string | null>(null);
  // Bumped whenever an admin action reaches a terminal status so the
  // activity panel below refetches and shows the row right away.
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  // MER-492: portal access code for the Users panel's credential
  // handoff screen. Read from the customers registry row (staff-only
  // admin surface); null in prod today, so the panel renders a
  // "not set" hint when absent.
  const [accessCode, setAccessCode] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setAccessCode(null);
    (async () => {
      try {
        // customers.slug is the canonical unique column (002 backfill);
        // id conventionally equals it but is not guaranteed to.
        const { data, error } = await getSupabase()
          .from("customers")
          .select("access_code")
          .eq("slug", slug)
          .maybeSingle();
        if (cancelled || error) return;
        const code = (data as { access_code?: unknown } | null)?.access_code;
        setAccessCode(typeof code === "string" && code.trim() !== "" ? code : null);
      } catch {
        // Best-effort read: the Users panel degrades to its
        // "not set" hint when the code cannot be fetched.
      }
    })();
    return () => {
      cancelled = true;
    };
    // activityRefreshKey: re-read after any terminal admin action so a
    // just-set access code (access_code_set) shows without a reload.
  }, [slug, activityRefreshKey]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, error: null }));

    (async () => {
      try {
        // listCustomers is the authoritative source for tenant id +
        // tier + services since it joins tenants table + bundled
        // config.yaml. Filtering client-side keeps the data layer
        // simple at N customers.
        const all = await listCustomers();
        const match = all.find((row) => row.slug === slug) ?? null;
        if (!match) {
          if (cancelled) return;
          setState({
            customer: null,
            entityHealth: [],
            recentEvents: [],
            isLoading: false,
            error: `No customer with slug "${slug}".`,
          });
          return;
        }
        const [entityHealth, recentEvents] = await Promise.all([
          fetchEntityHealth(match.tenantId),
          fetchRecentSyncEvents(match.tenantId, 20),
        ]);
        if (cancelled) return;
        setState({
          customer: match,
          entityHealth,
          recentEvents,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          customer: null,
          entityHealth: [],
          recentEvents: [],
          isLoading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function handleViewAs() {
    if (!state.customer) return;
    setImpersonating(true);
    setImpError(null);
    try {
      const result = await startImpersonation(state.customer.tenantId);
      navigate(`/${result.slug}`);
    } catch (err) {
      setImpError(err instanceof Error ? err.message : String(err));
      setImpersonating(false);
    }
  }

  return (
    <main style={{ padding: "24px 32px", display: "grid", gap: 18 }}>
      {state.isLoading ? <Skeleton label="Loading customer" height={180} /> : null}
      {state.error ? <ErrorCard message={state.error} /> : null}
      {!state.isLoading && !state.error && state.customer ? (
        <>
          <HeaderStrip
            customer={state.customer}
            onViewAs={handleViewAs}
            impersonating={impersonating}
            impError={impError}
          />
          <CommercialPanel tenantId={state.customer.tenantId} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              alignItems: "start",
            }}
          >
            <ConnectorsPanel entityHealth={state.entityHealth} />
            {/* key remounts the panel on customer switch so expand state
                and the per-entity history cache never leak across tenants
                (adversarial review finding); in-flight fetches resolve
                against the unmounted instance, a no-op. */}
            <EntityHealthPanel
              key={state.customer.tenantId}
              entityHealth={state.entityHealth}
              tenantId={state.customer.tenantId}
            />
          </div>
          <RecentIngestsPanel events={state.recentEvents} />
          {/* MER-487: write actions go through the admin_request_action
              queue; key remounts on customer switch so per-action state
              never leaks across tenants (the EntityHealthPanel precedent). */}
          <AdminActionsPanel
            key={`actions-${state.customer.tenantId}`}
            customer={state.customer}
            onActionTerminal={() => setActivityRefreshKey((k) => k + 1)}
          />
          {/* MER-492: tenant user management. Same remount-per-tenant +
              activity-refresh pattern as the action blocks above. */}
          <UsersPanel
            key={`users-${state.customer.tenantId}`}
            tenantId={state.customer.tenantId}
            slug={state.customer.slug}
            accessCode={accessCode}
            onActionTerminal={() => setActivityRefreshKey((k) => k + 1)}
          />
          <RecentAdminActivityPanel
            key={`activity-${state.customer.tenantId}`}
            slug={state.customer.slug}
            refreshKey={activityRefreshKey}
          />
        </>
      ) : null}
    </main>
  );
}

function HeaderStrip(props: {
  customer: CustomerRow;
  onViewAs: () => void;
  impersonating: boolean;
  impError: string | null;
}) {
  const { customer, onViewAs, impersonating, impError } = props;
  const tierLabel = customer.tier ? TIER_LABELS[customer.tier] : null;
  const tierTone = customer.tier ? TIER_TONE[customer.tier] : null;
  const services = customer.enabledServices;
  return (
    <header
      style={{
        padding: "22px 28px",
        background: "linear-gradient(110deg, var(--brand-mint) 0%, #E6F3EE 50%, #FFFFFF 100%)",
        border: "1px solid var(--brand-line)",
        borderRadius: 8,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 16,
        alignItems: "center",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "var(--text-2)",
            fontWeight: 700,
          }}
        >
          Admin / Customers
        </div>
        <h1
          style={{
            margin: "6px 0 0 0",
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: -0.5,
            color: "var(--text)",
          }}
        >
          {customer.name}
        </h1>
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            marginTop: 8,
            fontSize: 13,
            color: "var(--text-2)",
          }}
        >
          <span>/{customer.slug}</span>
          {tierTone && tierLabel ? (
            <span
              style={{
                display: "inline-block",
                fontSize: 10,
                padding: "4px 10px",
                borderRadius: 12,
                background: tierTone.bg,
                color: tierTone.fg,
                border: "1px solid var(--brand-line)",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontWeight: 700,
              }}
            >
              {tierLabel}
            </span>
          ) : null}
          <span title={services.map(humanize).join(", ")}>
            {services.length} service{services.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
        <button
          type="button"
          onClick={onViewAs}
          disabled={impersonating}
          style={impersonating ? viewAsBusyStyle : viewAsButtonStyle}
        >
          {impersonating ? "Switching..." : "View as"}
        </button>
        {impError ? (
          <span
            style={{ fontSize: 11, color: "var(--flag-red)", maxWidth: 260, textAlign: "right" }}
          >
            {impError}
          </span>
        ) : null}
      </div>
    </header>
  );
}

function ConnectorsPanel(props: { entityHealth: EntityHealthRow[] }) {
  const connectors = deriveConnectorHealth(props.entityHealth);
  return (
    <section style={panelStyle}>
      <h2 style={panelTitleStyle}>Connectors</h2>
      <div style={{ display: "grid", gap: 8 }}>
        {connectors.map((c) => (
          <ConnectorRow key={c.name} connector={c} />
        ))}
      </div>
    </section>
  );
}

function describeDegraded(e: ConnectorHealth["degraded"][number]): string {
  if (e.state === "missing") return `${e.key} (no sync recorded)`;
  if (e.state === "failed") return `${e.key} (last sync failed)`;
  return `${e.key} (last success ${relativeTime(e.lastSyncAt)})`;
}

function ConnectorRow(props: { connector: ConnectorHealth }) {
  const { connector } = props;
  // MER-675: an active connector with a dead sub-feed reads "Degraded" and
  // names the feed, instead of a green "Active" that hid it.
  const degraded = connector.active && connector.degraded.length > 0;
  const healthy = connector.active && !degraded;
  const badge = healthy
    ? "Active"
    : degraded
      ? "Degraded"
      : connector.lastSyncAt
        ? "Stale"
        : "Not configured";
  const badgeBg = healthy
    ? "rgba(46,160,67,0.12)"
    : degraded || connector.lastSyncAt
      ? "rgba(255,193,7,0.16)"
      : "rgba(0,0,0,0.06)";
  const badgeFg = healthy
    ? "var(--flag-green)"
    : degraded || connector.lastSyncAt
      ? "var(--flag-yellow)"
      : "var(--text-muted)";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 8,
        padding: "10px 12px",
        border: "1px solid var(--border-subtle)",
        borderRadius: 8,
        background: "var(--bg-card)",
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{connector.name}</div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
          {connector.description}
        </div>
        <div style={{ fontSize: 11, color: freshnessTone(connector.lastSyncAt), marginTop: 4 }}>
          Last sync {relativeTime(connector.lastSyncAt)}
        </div>
        {degraded ? (
          <div style={{ fontSize: 11, color: "var(--flag-yellow)", marginTop: 4 }}>
            Not syncing: {connector.degraded.map(describeDegraded).join(", ")}
          </div>
        ) : null}
      </div>
      <span
        style={{
          alignSelf: "center",
          fontSize: 10,
          padding: "3px 8px",
          borderRadius: 10,
          background: badgeBg,
          color: badgeFg,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 700,
        }}
      >
        {badge}
      </span>
    </div>
  );
}

function utcStamp(iso: string | null): string {
  if (!iso) return "(not recorded)";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "(not recorded)";
  return `${t.toISOString().replace("T", " ").slice(0, 16)} UTC`;
}

type HistoryState = RecentSyncEvent[] | "loading" | "error";

/**
 * MER-478: a Failed row used to hide error_detail behind a hover title
 * attr with no fix path. Failed rows are now click-expandable: full
 * error text, the failing attempt's started/completed stamps, a
 * remediation hint from the static error-class map, and the last 5
 * sync attempts for that entity.
 */
function EntityHealthPanel(props: { entityHealth: EntityHealthRow[]; tenantId: string }) {
  const rows = props.entityHealth;
  const { tenantId } = props;
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [historyByKey, setHistoryByKey] = useState<Record<string, HistoryState>>({});
  // MER-481: live table counts. undefined = loading, null = unavailable
  // (renders "-", never a fabricated 0). The panel is key-remounted per
  // tenant, so this state can't leak across customers.
  const [liveCounts, setLiveCounts] = useState<Record<string, number | null>>({});
  // MER-540: table_name -> {lastAnalyze, lastAutoanalyze}. Empty map
  // until loaded (or if the RPC fails) means "no staleness marker",
  // never a false-positive stale flag.
  const [tableStats, setTableStats] = useState<Map<string, TableStats>>(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [entries, stats] = await Promise.all([
        Promise.all(
          rows.map(async (r) => [r.entityKey, await fetchEntityRowCount(tenantId, r.entityKey)]),
        ),
        fetchTableStats(),
      ]);
      if (cancelled) return;
      setLiveCounts(Object.fromEntries(entries));
      setTableStats(stats);
    })();
    return () => {
      cancelled = true;
    };
    // rows.length (not rows) is deliberate: the panel is key-remounted
    // per tenant, so within one mount the entity SET only ever grows or
    // shrinks; identity-only changes must not refetch 18 counts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, rows.length]);

  function toggleExpand(row: EntityHealthRow) {
    const key = row.entityKey;
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    // "loading" stays cached: a re-click while the first fetch is in
    // flight must not start a second one.
    if (historyByKey[key] === undefined || historyByKey[key] === "error") {
      setHistoryByKey((h) => ({ ...h, [key]: "loading" }));
      fetchEntitySyncHistory(tenantId, key, 5)
        .then((events) => setHistoryByKey((h) => ({ ...h, [key]: events })))
        .catch(() => setHistoryByKey((h) => ({ ...h, [key]: "error" })));
    }
  }

  return (
    <section style={panelStyle}>
      <h2 style={panelTitleStyle}>Canonical entities</h2>
      {rows.length === 0 ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
          No syncs recorded yet. Run <code>meridian refresh {"<slug>"} --push</code> to populate.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={headerCellStyle}>Entity</th>
              <th style={{ ...headerCellStyle, textAlign: "right" }}>Rows in table</th>
              <th style={headerCellStyle}>Last sync</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const failed = r.lastSyncStatus === "failed";
              const expanded = expandedKey === r.entityKey;
              return (
                <FragmentRow
                  key={r.entityKey}
                  row={r}
                  failed={failed}
                  expanded={expanded}
                  liveCount={liveCounts[r.entityKey]}
                  stale={isEntityCountStale(tableStats, r.entityKey, r.lastSyncAt)}
                  history={historyByKey[r.entityKey]}
                  onToggle={() => toggleExpand(r)}
                />
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function FragmentRow(props: {
  row: EntityHealthRow;
  failed: boolean;
  expanded: boolean;
  liveCount: number | null | undefined;
  stale: boolean;
  history: HistoryState | undefined;
  onToggle: () => void;
}) {
  const { row: r, failed, expanded, liveCount, stale, history, onToggle } = props;
  return (
    <>
      <tr
        onClick={failed ? onToggle : undefined}
        style={failed ? { cursor: "pointer" } : undefined}
        aria-expanded={failed ? expanded : undefined}
      >
        <td style={cellStyle}>
          <span style={{ fontWeight: 600, color: "var(--text)" }}>{r.entityKey.toUpperCase()}</span>
          {failed ? (
            <>
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 10,
                  padding: "2px 6px",
                  borderRadius: 8,
                  background: "rgba(192,57,43,0.12)",
                  color: "var(--flag-red)",
                  fontWeight: 700,
                }}
              >
                Failed
              </span>
              <span aria-hidden style={{ marginLeft: 6, fontSize: 10, color: "var(--text-muted)" }}>
                {expanded ? "▾ hide detail" : "▸ show why"}
              </span>
            </>
          ) : null}
        </td>
        {/* MER-481: live table count, not the last attempt's upserted
            count. A failed sync on a populated table must never read
            as data loss. undefined = still loading. */}
        <td
          style={{ ...cellStyle, textAlign: "right" }}
          title={
            liveCount === null
              ? "Live count unavailable"
              : stale
                ? "Statistics predate the last sync; this estimate may undercount."
                : undefined
          }
        >
          {liveCount === undefined ? "..." : formatLiveRowCount(liveCount)}
          {/* MER-540: stats older than the last sync can undercount by a
              lot right after a big sync (the CUSTOMER ~3,070-vs-9,210
              incident) with no other visible signal. */}
          {stale && liveCount !== null && liveCount !== undefined ? (
            <span
              style={{
                marginLeft: 6,
                fontSize: 10,
                padding: "1px 6px",
                borderRadius: 8,
                background: "rgba(255,193,7,0.16)",
                color: "var(--flag-yellow)",
                fontWeight: 700,
              }}
            >
              stale
            </span>
          ) : null}
        </td>
        <td style={{ ...cellStyle, color: freshnessTone(r.lastSyncAt) }}>
          {relativeTime(r.lastSyncAt)}
        </td>
      </tr>
      {failed && expanded ? (
        <tr>
          <td colSpan={3} style={{ ...cellStyle, background: "rgba(192,57,43,0.04)" }}>
            <FailedRowDetail row={r} history={history} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function FailedRowDetail(props: { row: EntityHealthRow; history: HistoryState | undefined }) {
  const { row, history } = props;
  const remediation = classifySyncError(row.lastErrorDetail);
  return (
    <div style={{ display: "grid", gap: 10, padding: "2px 0 6px 0" }}>
      <div>
        <div style={failedDetailLabelStyle}>Error</div>
        <div
          style={{
            fontSize: 12,
            color: "var(--flag-red)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          {row.lastErrorDetail ?? "No error detail recorded for this attempt."}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
          Started {utcStamp(row.lastSyncStartedAt)} · Completed {utcStamp(row.lastSyncAt)} · Last
          attempt upserted {row.rowsUpserted?.toLocaleString() ?? "0"} row
          {row.rowsUpserted === 1 ? "" : "s"}
        </div>
      </div>
      {remediation ? (
        <div>
          <div style={failedDetailLabelStyle}>How to fix</div>
          <div style={{ fontSize: 12, color: "var(--text)" }}>
            {remediation.hint}
            {remediation.ticketRef ? (
              <span style={{ color: "var(--text-muted)" }}> (see {remediation.ticketRef})</span>
            ) : null}
          </div>
        </div>
      ) : null}
      <div>
        <div style={failedDetailLabelStyle}>Last 5 attempts</div>
        {history === undefined || history === "loading" ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading attempts...</div>
        ) : history === "error" ? (
          <div style={{ fontSize: 12, color: "var(--flag-red)" }}>
            Could not load sync history. Click the row to retry.
          </div>
        ) : history.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            No attempts recorded in sync_log.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 4 }}>
            {history.map((e) => (
              <div
                key={e.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "130px 64px 1fr",
                  gap: 8,
                  fontSize: 11,
                  alignItems: "baseline",
                }}
              >
                <span style={{ color: "var(--text-muted)" }}>
                  {utcStamp(e.completedAt ?? e.startedAt)}
                </span>
                <span
                  style={{
                    color: e.status === "success" ? "var(--flag-green)" : "var(--flag-red)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                  }}
                >
                  {e.status}
                </span>
                <span
                  style={{
                    color: e.errorDetail ? "var(--flag-red)" : "var(--text-muted)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={e.errorDetail ?? ""}
                >
                  {e.errorDetail ?? `${e.rowsUpserted.toLocaleString()} rows upserted`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const failedDetailLabelStyle: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  fontWeight: 700,
  marginBottom: 3,
};

function RecentIngestsPanel(props: { events: RecentSyncEvent[] }) {
  const events = props.events;
  return (
    <section style={panelStyle}>
      <h2 style={panelTitleStyle}>Recent ingests</h2>
      {events.length === 0 ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
          No ingest events recorded for this customer yet.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={headerCellStyle}>When</th>
              <th style={headerCellStyle}>Entity</th>
              <th style={headerCellStyle}>Status</th>
              <th style={{ ...headerCellStyle, textAlign: "right" }}>Upserted</th>
              <th style={{ ...headerCellStyle, textAlign: "right" }}>Deleted</th>
              <th style={headerCellStyle}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td style={cellStyle}>
                  <div style={{ color: "var(--text)" }}>
                    {relativeTime(e.completedAt ?? e.startedAt)}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                    {new Date(e.startedAt).toISOString().replace("T", " ").slice(0, 16)} UTC
                  </div>
                </td>
                <td style={cellStyle}>{e.entityKey.toUpperCase()}</td>
                <td style={cellStyle}>
                  <span
                    style={{
                      fontSize: 10,
                      padding: "2px 8px",
                      borderRadius: 8,
                      background:
                        e.status === "success" ? "rgba(46,160,67,0.12)" : "rgba(192,57,43,0.12)",
                      color: e.status === "success" ? "var(--flag-green)" : "var(--flag-red)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {e.status}
                  </span>
                </td>
                <td style={{ ...cellStyle, textAlign: "right" }}>
                  {e.rowsUpserted.toLocaleString()}
                </td>
                <td style={{ ...cellStyle, textAlign: "right", color: "var(--text-muted)" }}>
                  {e.rowsDeleted.toLocaleString()}
                </td>
                <td
                  style={{
                    ...cellStyle,
                    fontSize: 11,
                    color: e.errorDetail ? "var(--flag-red)" : "var(--text-muted)",
                    maxWidth: 280,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={e.errorDetail ?? ""}
                >
                  {e.errorDetail ?? ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * MER-487 PR C: queued write actions for one customer. Each block
 * enqueues an admin_action row via the admin_request_action RPC and
 * then renders an inline ActionStatusPanel that polls until the
 * droplet runner finishes. Re-submitting the same request after a
 * failure is safe: the runner skips steps that already completed.
 */
function useAdminAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);

  async function run(type: AdminActionType, payload: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    // Clear any prior action's panel up front: if this submit fails,
    // a stale status panel must not render beside the fresh error.
    setActionId(null);
    try {
      const res = await requestAdminAction(getSupabase(), type, payload);
      setActionId(res.actionId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return { busy, error, actionId, run };
}

const ADMIN_TIER_LABELS: Record<AdminTier, string> = {
  essential: "Essential",
  growth: "Growth",
  professional: "Pro",
  enterprise: "Enterprise",
};

function AdminActionsPanel(props: { customer: CustomerRow; onActionTerminal: () => void }) {
  const { customer, onActionTerminal } = props;
  return (
    <section style={panelStyle}>
      <h2 style={panelTitleStyle}>Admin actions</h2>
      <ConfigEditBlock customer={customer} onActionTerminal={onActionTerminal} />
      <AccessCodeBlock slug={customer.slug} onActionTerminal={onActionTerminal} />
      <DisableBlock slug={customer.slug} name={customer.name} onActionTerminal={onActionTerminal} />
    </section>
  );
}

function ConfigEditBlock(props: { customer: CustomerRow; onActionTerminal: () => void }) {
  const { customer, onActionTerminal } = props;
  const action = useAdminAction();
  // Pre-fill from the bundled config (tier + enabled services come from
  // listCustomers, which reads /manifests/<slug>-config.yaml).
  const [tier, setTier] = useState<AdminTier>(customer.tier ?? "growth");
  const coreIds = CORE_SERVICES.map((s) => s.id);
  const [services, setServices] = useState<string[]>(customer.enabledServices);
  const [serviceDraft, setServiceDraft] = useState("");
  const extraServices = services.filter((s) => !coreIds.includes(s));

  function toggleService(id: string) {
    setServices((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  }

  function addService() {
    const value = serviceDraft.trim().toLowerCase();
    if (value && !services.includes(value)) setServices((prev) => [...prev, value]);
    setServiceDraft("");
  }

  return (
    <div style={actionBlockStyle}>
      <div style={actionBlockTitleStyle}>Tier and services</div>
      <div style={actionBlockHintStyle}>
        Saving opens a pull request with the updated config. The change applies after that pull
        request is merged; the link appears below when it is ready.
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value as AdminTier)}
          style={inlineInputStyle}
        >
          {TIER_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {ADMIN_TIER_LABELS[t]}
            </option>
          ))}
        </select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
        {CORE_SERVICES.map((svc) => (
          <label key={svc.id} style={serviceCheckboxStyle}>
            <input
              type="checkbox"
              checked={services.includes(svc.id)}
              onChange={() => toggleService(svc.id)}
            />
            <span>{svc.label}</span>
          </label>
        ))}
        {extraServices.map((id) => (
          <label key={id} style={serviceCheckboxStyle}>
            <input type="checkbox" checked onChange={() => toggleService(id)} />
            <span>{humanize(id)}</span>
          </label>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="text"
          value={serviceDraft}
          onChange={(e) => setServiceDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addService();
            }
          }}
          placeholder="Add another service id"
          spellCheck={false}
          style={{ ...inlineInputStyle, flex: 1, maxWidth: 280 }}
        />
        <button type="button" onClick={addService} style={smallSecondaryButtonStyle}>
          Add
        </button>
        <button
          type="button"
          disabled={action.busy || services.length === 0}
          onClick={() =>
            action.run("config_edit", {
              slug: customer.slug,
              tier,
              enabled_services: services,
            })
          }
          style={action.busy ? smallPrimaryBusyStyle : smallPrimaryButtonStyle}
        >
          {action.busy ? "Saving..." : "Save changes"}
        </button>
      </div>
      {services.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--flag-red)" }}>Enable at least one service.</div>
      ) : null}
      {action.error ? (
        <div style={{ fontSize: 12, color: "var(--flag-red)" }}>{action.error}</div>
      ) : null}
      {action.actionId ? (
        <ActionStatusPanel
          actionId={action.actionId}
          onSummary={(s) => {
            if (s.terminal) onActionTerminal();
          }}
        />
      ) : null}
    </div>
  );
}

function AccessCodeBlock(props: { slug: string; onActionTerminal: () => void }) {
  const { slug, onActionTerminal } = props;
  const action = useAdminAction();
  const [code, setCode] = useState("");
  const codeEmpty = code.trim() === "";

  return (
    <div style={actionBlockStyle}>
      <div style={actionBlockTitleStyle}>Portal access code</div>
      <div style={actionBlockHintStyle}>
        Sets the code customers type on the upload portal. Replaces the current code right away.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="New access code"
          spellCheck={false}
          style={{ ...inlineInputStyle, flex: 1, maxWidth: 280 }}
        />
        <button
          type="button"
          disabled={action.busy || codeEmpty}
          onClick={() => action.run("access_code_set", { slug, access_code: code.trim() })}
          style={action.busy ? smallPrimaryBusyStyle : smallPrimaryButtonStyle}
        >
          {action.busy ? "Saving..." : "Set code"}
        </button>
      </div>
      {action.error ? (
        <div style={{ fontSize: 12, color: "var(--flag-red)" }}>{action.error}</div>
      ) : null}
      {action.actionId ? (
        <ActionStatusPanel
          actionId={action.actionId}
          onSummary={(s) => {
            if (s.terminal) onActionTerminal();
          }}
        />
      ) : null}
    </div>
  );
}

function DisableBlock(props: { slug: string; name: string; onActionTerminal: () => void }) {
  const { slug, name, onActionTerminal } = props;
  const action = useAdminAction();
  const [confirming, setConfirming] = useState(false);

  return (
    <div style={actionBlockStyle}>
      <div style={actionBlockTitleStyle}>Disable customer</div>
      <div style={actionBlockHintStyle}>
        Turns off dashboard access and scheduled refreshes for this customer. Data is kept.
      </div>
      {confirming ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text)" }}>
            Disable {name}? Their team loses dashboard access until re-enabled.
          </span>
          <button
            type="button"
            disabled={action.busy}
            onClick={() => {
              setConfirming(false);
              action.run("customer_disable", { slug });
            }}
            style={dangerButtonStyle}
          >
            Yes, disable
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            style={smallSecondaryButtonStyle}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div>
          <button
            type="button"
            disabled={action.busy}
            onClick={() => setConfirming(true)}
            style={dangerOutlineButtonStyle}
          >
            {action.busy ? "Submitting..." : "Disable customer"}
          </button>
        </div>
      )}
      {action.error ? (
        <div style={{ fontSize: 12, color: "var(--flag-red)" }}>{action.error}</div>
      ) : null}
      {action.actionId ? (
        <ActionStatusPanel
          actionId={action.actionId}
          onSummary={(s) => {
            if (s.terminal) onActionTerminal();
          }}
        />
      ) : null}
    </div>
  );
}

/** Plain-English labels for audit rows; falls back to humanize(). */
const AUDIT_ACTION_LABELS: Record<string, string> = {
  customer_create: "Created customer",
  customer_update: "Updated customer",
  customer_disable: "Disabled customer",
  access_code_set: "Set portal access code",
  config_edit: "Edited tier or services",
  tenant_user_invite: "Invited a user",
  tenant_user_role_set: "Changed a user role",
  tenant_user_revoke: "Revoked a user",
  tenant_user_handoff_done: "Marked credential handoff done",
};

type ActivityState =
  | { phase: "loading" }
  | { phase: "ready"; entries: AuditEntry[] }
  | { phase: "error"; message: string };

function RecentAdminActivityPanel(props: { slug: string; refreshKey: number }) {
  const { slug, refreshKey } = props;
  const [state, setState] = useState<ActivityState>({ phase: "loading" });

  // refreshKey bumps when an ActionStatusPanel above reports a terminal
  // summary, so a just-finished action shows up without a page reload.
  useEffect(() => {
    let cancelled = false;
    setState({ phase: "loading" });
    listAuditEntries(getSupabase(), slug, 20)
      .then((entries) => {
        if (!cancelled) setState({ phase: "ready", entries });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ phase: "error", message: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, refreshKey]);

  return (
    <section style={panelStyle}>
      <h2 style={panelTitleStyle}>Recent admin activity</h2>
      {state.phase === "loading" ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
          Loading activity...
        </div>
      ) : null}
      {state.phase === "error" ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--flag-red)" }}>
          Could not load admin activity: {state.message}
        </div>
      ) : null}
      {state.phase === "ready" && state.entries.length === 0 ? (
        <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
          No admin actions recorded for this customer yet.
        </div>
      ) : null}
      {state.phase === "ready" && state.entries.length > 0 ? (
        <div style={{ display: "grid", gap: 6 }}>
          {state.entries.map((entry, i) => (
            <div
              key={`${entry.occurredAt ?? "t"}-${i}`}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 8,
                padding: "8px 12px",
                border: "1px solid var(--border-subtle)",
                borderRadius: 8,
                fontSize: 13,
                alignItems: "baseline",
              }}
            >
              <div>
                <span style={{ fontWeight: 600, color: "var(--text)" }}>
                  {AUDIT_ACTION_LABELS[entry.actionType] ?? humanize(entry.actionType)}
                </span>
                <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>
                  by {entry.actorEmail ?? "unknown"}
                </span>
              </div>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {relativeTime(entry.occurredAt)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ErrorCard(props: { message: string }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        padding: 20,
        color: "var(--flag-red)",
        fontSize: 13,
      }}
    >
      {props.message}
    </div>
  );
}

const panelStyle: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: 18,
  display: "grid",
  gap: 12,
};

const panelTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--text-2)",
};

const cellStyle: CSSProperties = {
  padding: "10px 14px",
  fontSize: 13,
  borderBottom: "1px solid var(--border-subtle)",
  verticalAlign: "top",
};

const headerCellStyle: CSSProperties = {
  textAlign: "left",
  padding: "8px 14px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  borderBottom: "1px solid var(--border-subtle)",
  fontWeight: 700,
};

const viewAsButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  fontWeight: 700,
  padding: "8px 16px",
  borderRadius: 6,
  border: "1px solid var(--brand-deep)",
  background: "var(--brand-deep)",
  color: "#FFFFFF",
  cursor: "pointer",
};

const actionBlockStyle: CSSProperties = {
  display: "grid",
  gap: 8,
  padding: "12px 14px",
  border: "1px solid var(--border-subtle)",
  borderRadius: 8,
};

const actionBlockTitleStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: "var(--text)",
};

const actionBlockHintStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--text-muted)",
};

const inlineInputStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  padding: "7px 10px",
  borderRadius: 6,
  border: "1px solid var(--border-subtle)",
  background: "#FFFFFF",
  color: "var(--text)",
};

const serviceCheckboxStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  color: "var(--text)",
};

const smallSecondaryButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 600,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--brand-line)",
  background: "transparent",
  color: "var(--brand-deep)",
  cursor: "pointer",
};

const smallPrimaryButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 700,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--brand-deep)",
  background: "var(--brand-deep)",
  color: "#FFFFFF",
  cursor: "pointer",
};

const smallPrimaryBusyStyle: CSSProperties = {
  ...smallPrimaryButtonStyle,
  opacity: 0.7,
  cursor: "wait",
};

const dangerButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 700,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--flag-red)",
  background: "var(--flag-red)",
  color: "#FFFFFF",
  cursor: "pointer",
};

const dangerOutlineButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 700,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--flag-red)",
  background: "transparent",
  color: "var(--flag-red)",
  cursor: "pointer",
};

const viewAsBusyStyle: CSSProperties = {
  ...viewAsButtonStyle,
  opacity: 0.7,
  cursor: "wait",
};
